import { useCallback, useMemo, useRef, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import Bugsnag from '@bugsnag/js'
import { chunk } from 'lodash'
import {
  ABORT_MULTIPART_UPLOAD,
  COMPLETE_MULTIPART_UPLOAD,
  COMPLETE_UPLOAD,
  CREATE_MULTIPART_UPLOAD,
  GET_FILMMAKER_MULTIPART_UPLOAD_URL,
  GET_FILMMAKER_UPLOAD_URL,
  GET_MULTIPART_UPLOAD_URL,
  GET_UPLOAD_URL,
} from '../queries'

const CHUNK_BYTE_SIZE = 10_485_760 // 10MB
const CONCURRENT_UPLOADS = 10

interface Props {
  id: string
  type: 'APPLICATION' | 'UPDATE'
  fileName?: string
}

export const useMultipartUpload = ({
  id,
  type,
  fileName: optionalFileName,
}: Props): [(file: File) => Promise<void>, () => void, number] => {
  const [partsProgress, setPartsProgress] = useState<number[]>([])
  const xhrQueueRef = useRef<XMLHttpRequest[]>([])
  const uploadIdRef = useRef<string | null>(null)
  const fileRef = useRef<File | null>(null)

  const [createMultipartUpload] = useMutation(CREATE_MULTIPART_UPLOAD, {
    errorPolicy: 'all',
  })
  const [completeMultipartUpload] = useMutation(COMPLETE_MULTIPART_UPLOAD, {
    errorPolicy: 'all',
  })
  const [completeUpload] = useMutation(COMPLETE_UPLOAD, {
    errorPolicy: 'all',
  })
  const [abortMultipartUpload] = useMutation(ABORT_MULTIPART_UPLOAD, {
    errorPolicy: 'all',
  })

  const client = useApolloClient()

  const uploadXhr = useCallback((url?: string, part = 1) => {
    if (!url) {
      return Promise.reject(new Error('No upload url'))
    }

    return new Promise<{ part: number; eTag: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhrQueueRef.current.push(xhr)

      function removeXhr() {
        xhrQueueRef.current = xhrQueueRef.current.filter((xhr) => xhr !== xhr)
      }

      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', 'octet/stream')
      xhr.upload.onprogress = (event) => {
        setPartsProgress((current) => {
          const next = [...current]
          next[part] = Math.round((event.loaded / event.total) * 100)
          return next
        })
      }

      xhr.onload = () => {
        removeXhr()
        if (xhr.status === 200) {
          const eTag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') ?? ''
          resolve({ part, eTag })
        } else if (xhr.status !== 404) {
          reject(new Error(`Failed to upload file: ${xhr.statusText}`))
        }
      }

      const partIndex = part - 1
      const start = partIndex * CHUNK_BYTE_SIZE
      const end = Math.min(start + CHUNK_BYTE_SIZE, fileRef.current!.size)
      const blob = fileRef.current!.slice(start, end)

      xhr.send(blob)
      xhr.onerror = () => {
        removeXhr()
        reject(new Error(`Failed to upload file: ${xhr.statusText}`))
      }
      xhr.onabort = () => {
        removeXhr()
      }
    })
  }, [])

  const uploadMultipart = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (file: File) => {
      if (xhrQueueRef.current.length) {
        return Promise.reject(new Error('Upload already in progress'))
      }

      Bugsnag.leaveBreadcrumb('Starting Guild upload', {
        file: file.name,
        size: file.size,
      })

      fileRef.current = file
      const fileName = `${optionalFileName ? `<<|${optionalFileName}|>>` : ''}${file.name}`

      if (file.size < CHUNK_BYTE_SIZE) {
        const query = type === 'APPLICATION' ? GET_UPLOAD_URL : GET_FILMMAKER_UPLOAD_URL
        const variables = type === 'APPLICATION' ? { id, type, fileName } : { input: { id, type, fileName } }
        return client.query({ query, variables, errorPolicy: 'all' }).then((res) => {
          const url = res?.data?.torchApplication?.uploadUrl || res?.data?.filmmakerUploadUrl
          return uploadXhr(url).then(() => {
            if (type === 'UPDATE') {
              completeUpload({
                variables: {
                  input: {
                    id,
                    type,
                    fileName,
                  },
                },
              })
            }
          })
        })
      }

      const parts = Math.ceil(file.size / CHUNK_BYTE_SIZE)
      setPartsProgress(new Array(parts).fill(0))

      const uploadedParts: { part: number; eTag: string | null }[] = Array(parts)
        .fill(0)
        .map((_, i) => ({ part: i + 1, eTag: null }))

      return new Promise<void>((resolve, reject) => {
        return createMultipartUpload({
          variables: { input: { fileName, id, type } },
          async onCompleted(data) {
            const uploadId = data?.createFilmmakerMultipartUpload?.uploadId
            uploadIdRef.current = uploadId

            if (!uploadId) {
              reject(new Error('Failed to create multipart upload'))
              return
            }

            for await (const partsChunk of chunk(uploadedParts, CONCURRENT_UPLOADS)) {
              await Promise.all(
                partsChunk.map(({ part }) => {
                  const query = type === 'APPLICATION' ? GET_MULTIPART_UPLOAD_URL : GET_FILMMAKER_MULTIPART_UPLOAD_URL
                  const variables =
                    type === 'APPLICATION'
                      ? { uploadId, id, fileName, part, type }
                      : { input: { uploadId, id, fileName, part, type } }
                  return client
                    .query({ query, variables, fetchPolicy: 'network-only', errorPolicy: 'all' })
                    .then((res) => {
                      const url =
                        (res?.data?.torchApplication?.uploadMultipartUrl as string) ||
                        (res?.data?.filmmakerUploadMultipartUrl as string)
                      return uploadXhr(url, part)
                    })
                    .then((data) => {
                      uploadedParts[data.part - 1].eTag = data.eTag
                    })
                }),
              )
            }

            completeMultipartUpload({
              variables: {
                input: {
                  uploadId,
                  id,
                  fileName,
                  parts: uploadedParts,
                  type,
                },
              },
              onCompleted() {
                setPartsProgress([100])
                resolve()
              },
            }).catch((error) => {
              Bugsnag.notify(error)
              abortMultipartUpload({ variables: { input: { uploadId, id, fileName, type } } })
              xhrQueueRef.current.forEach((xhr) => xhr.abort())
              setPartsProgress([0])
              reject(error)
            })
          },
        })
      })
    },
    [
      abortMultipartUpload,
      client,
      completeMultipartUpload,
      createMultipartUpload,
      completeUpload,
      id,
      uploadXhr,
      type,
      optionalFileName,
    ],
  )

  const abort = useCallback(() => {
    xhrQueueRef.current.forEach((xhr) => xhr.abort())
    setPartsProgress([0])

    setTimeout(() => {
      abortMultipartUpload({
        variables: { input: { uploadId: uploadIdRef.current, id, fileName: fileRef.current?.name, type } },
      })
    })
  }, [abortMultipartUpload, id, type])

  const uploadProgress = useMemo(() => {
    const totalProgress = partsProgress.reduce((a, b) => a + (b || 0), 0) || 0

    if (totalProgress === 0 || !partsProgress.length) {
      return 0
    }

    return Math.floor(Math.round((totalProgress / partsProgress.length) * 100) / 100)
  }, [partsProgress])

  return [uploadMultipart, abort, uploadProgress]
}
